import React, { useState, useEffect, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import { getUserById, updateUserById } from "../firebase/Users";
import { DmsPermissions } from "../components/DmsPermissions";

export function UserDetails() {
  const { userId } = useParams();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [userData, setUserData] = useState(null);
  const initialValuesRef = useRef(null);

  useEffect(() => {
    let isMounted = true;
    getUserById(userId).then((rawData) => {
      if (isMounted && rawData) {
        const parsed = {
          email: rawData.email || "",
          name: rawData.name || "",
          language: rawData.language || "English",
          photoURL: rawData.photoURL || "",
          isAdmin: !!rawData.isAdmin,
          dms: {
            canAcceptDonation: !!rawData.dms?.canAcceptDonation, canAccess: !!rawData.dms?.canAccess,
            canCreateDonor: !!rawData.dms?.canCreateDonor, canUpdateDonor: !!rawData.dms?.canUpdateDonor,
            canAccessReports: !!rawData.dms?.canAccessReports,
          },
          taskRole:
            rawData.taskRole === "No Access"
              ? "No Access"
              : rawData.taskRole === "TaskAdmin"
              ? "TaskAdmin"
              : "TaskUser",
          status: rawData.status || "Active",
        };
        setUserData(parsed);
        initialValuesRef.current = parsed;
      }
      if (isMounted) setLoading(false);
    }).catch(() => {
      if (isMounted) setLoading(false);
    });
    return () => { isMounted = false; };
  }, [userId]);

  if (loading) {
    return (
      <div className="container mt-4 text-center">
        <div className="spinner-border text-primary" role="status"><span className="visually-hidden">Loading...</span></div>
        <p className="mt-2">Loading user details...</p>
      </div>
    );
  }

  if (!userData) {
    return (
      <div className="container mt-4">
        <div className="alert alert-danger text-center" role="alert">User not found.</div>
      </div>
    );
  }

  const getChangedData = () => {
    const initial = initialValuesRef.current;
    if (!initial) return null;
    const changed = {};
    ["isAdmin", "taskRole", "status"].forEach((key) => {
      if (userData[key] !== initial[key]) changed[key] = userData[key];
    });
    const dmsKeys = ["canAcceptDonation", "canAccess", "canCreateDonor", "canUpdateDonor", "canAccessReports"];
    if (dmsKeys.some((key) => userData.dms[key] !== initial.dms[key])) {
      changed.dms = { ...userData.dms };
    }
    return Object.keys(changed).length > 0 ? changed : null;
  };

  const changedData = getChangedData();
  const hasChanges = !!changedData;

  const handleSave = async (e) => {
    e.preventDefault();
    if (!hasChanges || saving) return;
    setSaving(true);
    try {
      await updateUserById(userId, changedData);
      initialValuesRef.current = { ...userData };
      alert("User details updated successfully.");
    } catch (err) {
      alert("Failed to update user details.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="container mt-4 mb-5">
      <div className="card p-4 shadow-sm">
        <div className="d-flex justify-content-between align-items-center mb-4 border-bottom pb-2">
          <h2 className="m-0">User Details</h2>
          <Link to="/searchuser" className="btn btn-secondary btn-sm">Back to Search</Link>
        </div>

        <form onSubmit={handleSave}>
          <div className="row mb-4">
            <div className="col-md-3 text-center mb-3 d-flex align-items-center justify-content-center">
              {userData.photoURL ? (
                <img src={userData.photoURL} alt={userData.name} className="rounded-circle img-thumbnail object-cover w-28 h-28 mx-auto d-block" width="112" height="112" />
              ) : (
                <div className="rounded-circle img-thumbnail bg-light d-flex align-items-center justify-content-center mx-auto w-28 h-28">
                  <span className="text-muted small">No Image</span>
                </div>
              )}
            </div>

            <div className="col-md-9">
              <div className="row">
                <div className="col-md-6 mb-3">
                  <label className="form-label fw-bold m-1">Name</label>
                  <input type="text" className="form-control p-2" value={userData.name} disabled />
                </div>
                <div className="col-md-6 mb-3">
                  <label className="form-label fw-bold m-1">Email</label>
                  <input type="email" className="form-control p-2" value={userData.email} disabled />
                </div>
              </div>

              <div className="row">
                <div className="col-md-6 mb-3">
                  <label className="form-label fw-bold m-1">Language</label>
                  <input type="text" className="form-control p-2" value={userData.language} disabled />
                </div>
              </div>
            </div>
          </div>

          <hr className="my-4" />

          <div className="mb-4">
            <h4 className="mb-3">Account Status & Roles</h4>
            <div className="row align-items-center">
              <div className="col-md-4 mb-3">
                <label className="form-label fw-bold m-1" htmlFor="userStatus">Status</label>
                <select
                  id="userStatus"
                  className="form-select form-control p-2"
                  value={userData.status}
                  onChange={(e) => setUserData((prev) => ({ ...prev, status: e.target.value }))}
                >
                  <option value="Active">Active</option>
                  <option value="Inactive">Inactive</option>
                </select>
              </div>
              <div className="col-md-4 mb-3">
                <label className="form-label fw-bold m-1" htmlFor="userTaskRole">Task Role</label>
                <select
                  id="userTaskRole"
                  className="form-select form-control p-2"
                  value={userData.taskRole}
                  onChange={(e) => setUserData((prev) => ({ ...prev, taskRole: e.target.value }))}
                >
                  <option value="TaskUser">TaskUser</option>
                  <option value="TaskAdmin">TaskAdmin</option>
                  <option value="No Access">No Access</option>
                </select>
              </div>
              <div className="col-md-4 mb-3 pt-3">
                <div className="form-check">
                  <input
                    type="checkbox"
                    className="form-check-input"
                    id="isAdmin"
                    checked={userData.isAdmin}
                    onChange={() => setUserData((prev) => ({ ...prev, isAdmin: !prev.isAdmin }))}
                  />
                  <label className="form-check-label m-0 ms-2 fw-bold" htmlFor="isAdmin">Is Admin</label>
                </div>
              </div>
            </div>
          </div>

          <hr className="my-4" />

          <DmsPermissions dms={userData.dms} onChange={(newDms) => setUserData((prev) => ({ ...prev, dms: newDms }))} />

          <div className="text-center mt-4 border-top pt-3">
            {saving ? (
              <button className="btn btn-primary px-4" disabled>
                <span className="spinner-grow spinner-grow-sm me-2" role="status" aria-hidden="true"></span>
                Saving…
              </button>
            ) : (
              <button type="submit" className="btn btn-primary px-4" disabled={!hasChanges}>
                Save Changes
              </button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
}

export default UserDetails;
